import React from "react";
import Modal from "../../elements/Modal";
import MultiselectDropdown from "../../elements/MultiselectDropdown";
import MultiselectDropdownWithBreakdown from "../../elements/MultiselectDropdownWithBreakdown";
import styles from "../AddReportPopup/index.module.css";
import myStyles from "../AddReportProduct/index.module.css";
import { useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filterLocationsByOrg } from "../../../utils/helpers";
import { closeEdit } from "../../../store/slices/addReportDutyFree/popup.slice";
import { updateReportAsyncThunk } from "../../../store/slices/addReportDutyFree/productYourReport.slice";
import { productAxios } from "../../../utils/axios/product.axios";
import {
  categorySteps,
  brandSteps,
  locationSteps,
} from "../../../utils/constant";

const EditReportProduct = () => {
  const dispatch = useDispatch();
  const { productYourReports } = useSelector(
    (store) => store.productYourReportsDutyFree
  ); // List of your stories.
  const constants = useSelector((store)=>store.constant);
  const { source } = useSelector((store) => store.constant);
  const organizationId = useSelector((store) => store.user.organizationId);
  const { showEditReport } = useSelector((store) => store.addReport); // hide/show edit popup.
  
  const filteredLocations = useMemo(() => {
    return filterLocationsByOrg(constants.location, organizationId);
  }, [constants.location, organizationId]);

  const [reportName, setReportName] = useState(
    productYourReports[showEditReport.reportIndex].name
  );
  const [selectedCategory, setSelectedCategory] = useState(
    productYourReports[showEditReport.reportIndex].categories?.split(",")
  );
  const [selectedSubCategory, setSelectedSubCategory] = useState(
    productYourReports[showEditReport.reportIndex].subcategories?.split(",") || []
  );
  const [selectedBrand, setSelectedBrand] = useState(
    productYourReports[showEditReport.reportIndex].brands?.split(",")
  );
  const [selectedLocation, setSelectedLocation] = useState(
    productYourReports[showEditReport.reportIndex].locations?.split(",")
  );
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const productSearchDebounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  const mapProductDataToOptions = (data, src) => {
    if (!Array.isArray(data)) return [];
    const seen = new Set();
    return data
      .filter((p) => p.canprod_id && !seen.has(p.canprod_id) && seen.add(p.canprod_id))
      .map((p) => {
        const sp = p.products_data?.find((pd) => pd.website === src);
        const title = sp?.title || "";
        const sku = sp?.sku || "";
        const label = title + (sku ? ` (SKU: ${sku})` : "");
        return { canprod_id: p.canprod_id, title, sku, label };
      });
  };

  useEffect(() => {
    const report = productYourReports[showEditReport.reportIndex];
    const canprodIdsStr = report?.canprod_ids;
    if (!canprodIdsStr || !source) {
      setSelectedProducts([]);
      return;
    }
    const ids = canprodIdsStr.split(",").map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) {
      setSelectedProducts([]);
      return;
    }
    productAxios
      .get("", {
        params: { source, canprod_ids: ids.join(","), limit: ids.length + 10, offset: 0 },
      })
      .then((res) => {
        const list = res?.data || [];
        setSelectedProducts(mapProductDataToOptions(list, source));
      })
      .catch(() => setSelectedProducts([]));
  }, [showEditReport.reportIndex, source]);

  useEffect(() => {
    if (!source || !productSearchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (productSearchDebounceRef.current) clearTimeout(productSearchDebounceRef.current);
    productSearchDebounceRef.current = setTimeout(() => {
      setProductSearchLoading(true);
      productAxios
        .get("", {
          params: {
            source,
            search: productSearchQuery.trim(),
            limit: 5,
            offset: 0,
          },
        })
        .then((res) => {
          const list = res?.data || [];
          const opts = mapProductDataToOptions(list, source);
          const selectedIds = new Set(selectedProducts.map((p) => p.canprod_id));
          setSuggestions(opts.filter((o) => !selectedIds.has(o.canprod_id)).slice(0, 5));
          setShowSuggestions(true);
        })
        .catch(() => {
          setSuggestions([]);
          setShowSuggestions(false);
        })
        .finally(() => setProductSearchLoading(false));
    }, 300);
    return () => {
      if (productSearchDebounceRef.current) clearTimeout(productSearchDebounceRef.current);
    };
  }, [productSearchQuery, source]);

  const onSelectProduct = (product) => {
    setSelectedProducts((prev) => [...prev, product]);
    setSuggestions((prev) => prev.filter((s) => s.canprod_id !== product.canprod_id));
    /* keep dropdown open and keep query so user can select multiple without reopening search */
  };

  const onRemoveProduct = (canprodId) => {
    setSelectedProducts((prev) => prev.filter((p) => p.canprod_id !== canprodId));
  };

  // Format subcategories for hierarchical display
  const formatStepsForDropdown = (options) => {
    // Check if source is one of the New Zealand sources
    const nzSources = ['aelia_auckland', 'aelia_queenstown', 'aelia_christchurch'];
    
    if (nzSources.includes(source)) {
      // Return current output for New Zealand sources
      return [
        {
          heading: "beauty",
          options: ["fragrance", "gift_sets", "makeup", "skin_care"],
        },
        {
          heading: "spirits",
          options: [
            "baijiu",
            "brandy",
            "cognac",
            "gin",
            "liqueurs",
            "mini_bottles",
            "premium_spirits",
            "rum",
            "tequila",
            "vodka",
            "whisky",
          ],
        },
        {
          heading: "wine",
          options: [
            "fortified",
            "premium_wine",
            "red",
            "rose",
            "sparkling_champagne",
            "white",
          ],
        },
        {
          heading: "confectionery",
          options: [
            "chocolates",
            "food_staples",
            "health_food",
            "lollies",
            "mints",
            "snacks_and_sweets",
            "souvenir_food",
          ],
        },
      ];
    } else if (source === 'heinemann_sydney') {
      // Return specific output for heinemann_sydney
      return [
        {
          heading: "whisky",
          options: [
            "american_whisky",
            "blended_whisky",
            "canadian_whisky",
            "irish_whisky",
            "japanese_whisky",
            "scotch_whisky",
            "single_malt_whisky"
          ]
        },
        {
          heading: "rum",
          options: ["rum"]
        },
        {
          heading: "wine",
          options: ["red_wine", "rose_wine", "sparkling_wine"]
        },
        {
          heading: "liqueur & aperitif",
          options: ["bitter_aperitif", "liqueur", "sherry_port"]
        },
        {
          heading: "cognac & brandy",
          options: ["cognac_brandy"]
        },
        {
          heading: "gin",
          options: ["gin"]
        },
        {
          heading: "vodka",
          options: ["vodka"]
        },
        {
          heading: "tequila",
          options: ["tequila"]
        },
        {
          heading: "champagne",
          options: ["champagne"]
        },
        {
          heading: "beauty",
          options: [
            "blusher",
            "concealer",
            "eye_shadow",
            "eyebrows",
            "eyeliner",
            "foundation",
            "lipstick_and_lipliner",
            "makeup_sets",
            "mascara",
            "powders"
          ]
        },
        {
          heading: "skin_care",
          options: [
            "bath_and_shower",
            "body_care",
            "bodycare_care",
            "cleansing",
            "eye_care",
            "hair_care",
            "hand_care",
            "masks",
            "men_skincare",
            "serum",
            "skin_care_sets",
            "sun_care",
            "care"
          ]
        },
        {
          heading: "fragrance",
          options: [
            "home_fragrance_candle",
            "men_fragrance",
            "men_fragrance_set",
            "women_fragrance",
            "women_fragrance_set"
          ]
        },
        {
          heading: "toiletries",
          options: ["toiletries"]
        },
        {
          heading: "accessories",
          options: ["accessories"]
        }
      ];
    } else {
      // Default fallback for other sources
      return [
        {
          heading: "beauty",
          options: ["fragrance", "gift_sets", "makeup", "skin_care"],
        },
        {
          heading: "spirits",
          options: [
            "baijiu",
            "brandy",
            "cognac",
            "gin",
            "liqueurs",
            "mini_bottles",
            "premium_spirits",
            "rum",
            "tequila",
            "vodka",
            "whisky",
          ],
        },
        {
          heading: "wine",
          options: [
            "fortified",
            "premium_wine",
            "red",
            "rose",
            "sparkling_champagne",
            "white",
          ],
        },
        {
          heading: "confectionery",
          options: [
            "chocolates",
            "food_staples",
            "health_food",
            "lollies",
            "mints",
            "snacks_and_sweets",
            "souvenir_food",
          ],
        },
      ];
    }
  };

  const flattenOptions = (groupedOptions) => {
    return groupedOptions.flatMap((group) => group.options);
  };

  // Manage Category selection.
  const onSelectedCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  // Manage SubCategory selection.
  const onSelectedSubCategoryChange = (value) => {
    setSelectedSubCategory(value);
  };

  // Mange Brand selection.
  const onSelectedBrandChange = (value) => {
    setSelectedBrand(value);
  };

  // Mange Location selection.
  const onSelectedLocationChange = (value) => {
    setSelectedLocation(value);
  };

  // Handles title input
  const onChangeTitle = (e) => {
    setReportName(e.target.value);
  };

  // Handle cancel button.
  const showAddReport = () =>
    dispatch(closeEdit({ show: false, reportIndex: -1 }));

  // Handle update.
  const onUpdate = () => {
    let err = 0;
    if (reportName.length == 0) {
      err++;
      alert("Name field is requied!");
    } else if (selectedCategory.length == 0) {
      err++;
      alert("Select atleast one Category!");
    } else if (selectedSubCategory.length == 0) {
      err++;
      alert("Select atleast one SubCategory!");
    } else if (selectedBrand.length == 0) {
      err++;
      alert("Select atleast one Brand!");
    } else if (selectedLocation.length == 0) {
      err++;
      alert("Select atleast one Location!");
    }

    if (err == 0) {
      let locations = [...selectedLocation];
      if (!locations.includes(source)) {
        locations.push(source);
      }
      const canprodIds = selectedProducts.length > 0
        ? selectedProducts.map((p) => p.canprod_id).join(",")
        : undefined;
      dispatch(
        updateReportAsyncThunk({
          index: showEditReport.reportIndex,
          title: reportName,
          category: selectedCategory,
          subcategory: selectedSubCategory,
          brand: selectedBrand,
          location: locations,
          id: productYourReports[showEditReport.reportIndex].id,
          canprod_ids: canprodIds,
        })
      );
      showAddReport();
    }
  };
  return (
    <Modal>
      <div className={styles["content"]}>
        {/* Heading */}
        <div className={styles["heading"]}>
          <h3>Update report</h3>
          <p>Update your customized reports</p>
        </div>

        {/* Name input */}
        <div className={styles["nameInput"]}>
          <label htmlFor="">Name</label>
          <input
            type="text"
            placeholder="List of products in Landside"
            value={reportName}
            onChange={onChangeTitle}
          />
        </div>

        {/* Products (optional) - label first, then selected chips, then search input */}
        <div className={styles["nameInput"] + " " + styles["productSearchWrap"] + (showSuggestions ? " " + styles["productSearchWrapOpen"] : "")}>
          <label htmlFor="product-search-edit">Products (optional)</label>
          {selectedProducts.length > 0 && (
            <div className={styles["selectedProducts"]}>
              {selectedProducts.map((p) => (
                <span key={p.canprod_id} className={styles["selectedProductChip"]}>
                  {p.title}
                  <button type="button" className={styles["selectedProductRemove"]} onClick={() => onRemoveProduct(p.canprod_id)} aria-label="Remove">×</button>
                </span>
              ))}
            </div>
          )}
          <div className={styles["productSearchInputWrap"]}>
            <input
              id="product-search-edit"
              type="text"
              placeholder="Search by product name or SKU"
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              autoComplete="off"
            />
            {showSuggestions && (
              <ul ref={suggestionsRef} className={styles["productSuggestions"]}>
                {productSearchLoading ? (
                  <li className={styles["productSuggestionItem"]}>Searching...</li>
                ) : suggestions.length === 0 ? (
                  <li className={styles["productSuggestionItem"]}>No products found</li>
                ) : (
                  suggestions.map((p) => (
                    <li
                      key={p.canprod_id}
                      className={styles["productSuggestionItem"]}
                      onMouseDown={(e) => { e.preventDefault(); onSelectProduct(p); }}
                    >
                      {p.label}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Category, location, and brand. */}
        <div
          className={styles["terminalStore"] + " " + myStyles["allDropdown"]}
        >
          <div className={styles["dropdown"] + " " + myStyles["dropdown"]}>
            <div className={styles["title"]}>
              <span>Category</span>
              <i class="bi bi-columns-gap"></i>
            </div>
            <MultiselectDropdown
              steps={categorySteps}
              selSteps={productYourReports[showEditReport.reportIndex].categories.split(",")}
              name=""
              onChange={onSelectedCategoryChange}
            />
          </div>

          <div className={styles["dropdown"] + " " + myStyles["dropdown"]}>
            <div className={styles["title"]}>
              <span>SubCategory</span>
              <i class="bi bi-layers"></i>
            </div>
              <MultiselectDropdownWithBreakdown
                steps={formatStepsForDropdown(constants.sub_category)}
                selSteps={
                  productYourReports[showEditReport.reportIndex].subcategories?.split(",") || 
                  flattenOptions(formatStepsForDropdown(constants.sub_category))
                }
                name=""
                onChange={onSelectedSubCategoryChange}
              />
          </div>

          <div className={styles["dropdown"] + " " + myStyles["dropdown"]}>
            <div className={styles["title"]}>
              <span>Brand</span>
              <i class="bi bi-tag"></i>
            </div>

            <MultiselectDropdown
              steps={constants?.brand}
              selSteps={productYourReports[showEditReport.reportIndex].brands?.split(",")}
              name=""
              onChange={onSelectedBrandChange}
            />
          </div>
          <div className={styles["dropdown"] + " " + myStyles["dropdown"]}>
            <div className={styles["title"]}>
              <span>Location</span>
              <i class="bi bi-pin"></i>
            </div>

            <MultiselectDropdown
              steps={filteredLocations}
              selSteps={productYourReports[showEditReport.reportIndex].locations?.split(",")}
              name=""
              onChange={onSelectedLocationChange}
            />
          </div>
        </div>

        {/* Create and cancel button. */}
        <div className={styles["createCancel"]}>
          <div className={styles["create"]} onClick={onUpdate}>
            Update
          </div>
          <div className={styles["cancel"]} onClick={showAddReport}>
            Cancel
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditReportProduct;
